import React from "react";
import { useWebinars } from "../hooks/useWebinars";
import { Webinar } from "../types/webinars";

/**
 * Formats a CMS webinar date (M/D/YYYY) for display.
 */
export function formatWebinarDate(dateStr: string): string {
    const parts = dateStr.split("/").map((part) => parseInt(part, 10));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
        return dateStr;
    }

    const [month, day, year] = parts;
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) {
        return dateStr;
    }

    return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

function WebinarCard({ webinar }: { webinar: Webinar }) {
    return (
        <article className="landing-webinar-item">
            <h3 className="landing-webinar-title">{webinar.title}</h3>

            <time className="landing-webinar-date" dateTime={webinar.webinarDate}>
                {formatWebinarDate(webinar.webinarDate)}
            </time>

            {(webinar.speakerImage || webinar.speakerName) && (
                <div className="landing-webinar-speaker-row">
                    {webinar.speakerImage && (
                        <img
                            src={webinar.speakerImage}
                            alt={webinar.speakerName ? `${webinar.speakerName.trim()} headshot` : ""}
                            className="landing-webinar-speaker-avatar"
                            loading="lazy"
                        />
                    )}
                    {webinar.speakerName && (
                        <p className="landing-webinar-speaker">{webinar.speakerName.trim()}</p>
                    )}
                </div>
            )}

            {webinar.description && (
                <p className="landing-webinar-description">{webinar.description}</p>
            )}

            <a
                className="landing-webinar-register-btn"
                href={webinar.buttonUrl}
                target="_blank"
                rel="noopener noreferrer"
            >
                {webinar.buttonText.trim()}
            </a>
        </article>
    );
}

/**
 * Homepage "Upcoming Webinars" section.
 *
 * Renders CMS-driven webinar cards with speaker imagery, event details, and a
 * registration link that opens in a new tab. When the CMS returns no upcoming
 * webinars (or is unavailable) nothing is rendered.
 */
export function UpcomingWebinars() {
    const { webinars, loading, error } = useWebinars();

    if (loading || error || webinars.length === 0) {
        return null;
    }

    return (
        <section className="landing-webinars" aria-label="Upcoming Webinars">
            <div className="landing-webinars-card">
                <header className="landing-webinars-header">
                    <p className="landing-section-label landing-webinars-label">
                        <span className="landing-section-label-diamond">✦</span> Join our community
                    </p>
                    <h2 className="landing-webinars-heading">Upcoming Webinars</h2>
                </header>

                <div className="landing-webinars-grid">
                    {webinars.map((webinar) => (
                        <WebinarCard key={webinar.id} webinar={webinar} />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default UpcomingWebinars;
